// Paiement en ligne initié au guichet par un agent (POST /recettes/paiements) sur une ou plusieurs
// créances ouvertes d'un même redevable, montant au plus égal au reste ; le prestataire est l'un des
// adaptateurs configurés (seul le simulateur existe) et la transaction se pilote ensuite depuis le
// simulateur, affiché dans la même fenêtre quand c'est lui.
import { Bandeau, Bouton, CaseACocher, Champ, Dialogue, GrilleChamps, Selection } from '@cc/ui';
import { CreditCard } from 'lucide-react';
import { useState } from 'react';
import { requete } from '../../api/client';
import { useAction } from '../../commun/useAction';
import { Lien } from '@cc/ui';
import { LIBELLES_ETAT_PRESTATAIRE, francs, libelle, type CreanceReponse, type PaiementReponse } from './donnees';
import { INVALIDER_RECETTES, useCreances, usePrestataires, useRedevables } from './requetes';
import { Simulateur } from './Simulateur';
import styles from './recettes.module.css';

interface Initiation {
  paiement: PaiementReponse;
  url_paiement: string | null;
}

const ouverte = (c: CreanceReponse) => c.etat === 'emise' || c.etat === 'partiellement_payee';

function Choix({ creance, onChoisir }: { creance?: CreanceReponse; onChoisir: (ids: string[], reste: number, partielAdmis: boolean) => void }) {
  const [redevable, setRedevable] = useState('');
  const [cochees, setCochees] = useState<string[]>([]);
  const redevables = useRedevables();
  const creances = useCreances({ etat: '', echue: '', rubrique: '', nature: '', redevable_id: redevable }, Boolean(redevable) && !creance);
  const candidates = (creances.data ?? []).filter(ouverte);
  const choisir = (ids: string[]) => {
    setCochees(ids);
    const choisies = candidates.filter((c) => ids.includes(c.id));
    onChoisir(ids, choisies.reduce((s, c) => s + c.reste, 0), choisies.every((c) => c.rubrique.paiement_partiel_autorise));
  };
  if (creance) return null;
  return (
    <>
      <Selection id="guichet-redevable" libelle="Redevable" required optionVide="Choisir" options={(redevables.data ?? []).filter((r) => r.reste > 0).map((r) => ({ valeur: r.id, libelle: `${r.nom} (reste ${francs(r.reste)})` }))} value={redevable} onChange={(e) => { setRedevable(e.target.value); choisir([]); }} />
      {redevable ? (
        <fieldset>
          <legend>Créances à payer</legend>
          {candidates.length === 0 ? <p className={styles.note}>{creances.isPending ? 'Lecture des créances…' : 'Aucune créance ouverte pour ce redevable.'}</p> : null}
          {candidates.map((c) => (
            <CaseACocher key={c.id} id={`guichet-${c.id}`} libelle={`${c.numero}, ${c.libelle}, reste ${francs(c.reste)}`} checked={cochees.includes(c.id)} onChange={(e) => choisir(e.target.checked ? [...cochees, c.id] : cochees.filter((x) => x !== c.id))} />
          ))}
        </fieldset>
      ) : null}
    </>
  );
}

/**
 * Bouton et fenêtre du paiement au guichet. `masque` cache le bouton (créance close) sans fermer une
 * fenêtre déjà ouverte : le résultat du simulateur reste lisible quand le paiement solde la créance.
 */
export function PaiementGuichet({ creance, petit, masque }: { creance?: CreanceReponse; petit?: boolean; masque?: boolean }) {
  const [ouvert, setOuvert] = useState(false);
  const [ids, setIds] = useState<string[]>(creance ? [creance.id] : []);
  const [reste, setReste] = useState(creance?.reste ?? 0);
  const [partielAdmis, setPartielAdmis] = useState(creance?.rubrique.paiement_partiel_autorise ?? true);
  const [montant, setMontant] = useState(creance ? String(creance.reste) : '');
  const [prestataire, setPrestataire] = useState('');
  const [initie, setInitie] = useState<Initiation | null>(null);
  const prestataires = usePrestataires(ouvert);
  const action = useAction<undefined, Initiation>({
    executer: () => requete<Initiation>('/recettes/paiements', { methode: 'POST', corps: { creance_ids: ids, montant: Number(montant.replace(/\s/g, '')), ...(prestataire ? { prestataire } : {}) } }),
    succes: (r) => `Paiement ${r.paiement.reference_serveur} initié, en attente du prestataire.`,
    invalider: INVALIDER_RECETTES,
    apres: (r) => setInitie(r),
  });
  /** À chaque ouverture, la fenêtre repart du reste à payer courant de la créance (et non de la saisie précédente). */
  const ouvrir = () => {
    setIds(creance ? [creance.id] : []);
    setReste(creance?.reste ?? 0);
    setPartielAdmis(creance?.rubrique.paiement_partiel_autorise ?? true);
    setMontant(creance ? String(creance.reste) : '');
    setPrestataire('');
    setInitie(null);
    action.reinitialiser();
    setOuvert(true);
  };
  const fermer = () => {
    setOuvert(false);
    setInitie(null);
    action.reinitialiser();
  };
  const n = Number(montant.replace(/\s/g, ''));
  const valide = ids.length > 0 && Number.isInteger(n) && n > 0 && n <= reste && (partielAdmis || n === reste);
  const simulateur = initie?.url_paiement?.includes('/public/simulateur-paiement/') ?? false;
  return (
    <>
      {masque ? null : (
        <Bouton variante="principal" icone={CreditCard} petit={petit} onClick={ouvrir}>
          {creance ? 'Encaisser au guichet' : 'Paiement au guichet'}
        </Bouton>
      )}
      <Dialogue
        ouvert={ouvert}
        titre={initie ? `Paiement ${initie.paiement.reference_serveur}` : 'Paiement au guichet'}
        onFermer={fermer}
        actions={
          initie ? (
            <Bouton variante="secondaire" onClick={fermer}>
              Fermer
            </Bouton>
          ) : (
            <>
              <Bouton variante="secondaire" onClick={fermer}>
                Annuler
              </Bouton>
              <Bouton chargement={action.enCours} disabled={!valide} onClick={() => action.lancer(undefined)}>
                Initier le paiement
              </Bouton>
            </>
          )
        }
      >
        {initie ? (
          <div className={styles.pile}>
            <p>
              Paiement en ligne initié pour {francs(initie.paiement.montant)} ; état : en attente du prestataire. La créance ne bouge qu&apos;à la confirmation vérifiée par le serveur.{' '}
              <Lien href={`/recettes/paiements/${initie.paiement.id}`}>Ouvrir la fiche du paiement</Lien>.
            </p>
            {simulateur ? <Simulateur reference={initie.paiement.reference_serveur} /> : <p className={styles.note}>Le prestataire confirmera ou refusera la transaction par sa notification signée.</p>}
          </div>
        ) : (
          <>
            <p>{creance ? `Créance ${creance.numero}, reste à payer ${francs(creance.reste)}.` : 'Choisissez le redevable puis les créances ouvertes à payer ensemble.'} Le paiement partiel n&apos;est admis que si chaque rubrique l&apos;autorise.</p>
            <Choix creance={creance} onChoisir={(i, r, p) => { setIds(i); setReste(r); setPartielAdmis(p); setMontant(String(r)); }} />
            <GrilleChamps>
              <Champ id="guichet-montant" libelle="Montant (F)" aide={`Au plus ${francs(reste)}${partielAdmis ? '' : ' ; paiement partiel refusé par la rubrique'}`} inputMode="numeric" required value={montant} onChange={(e) => setMontant(e.target.value)} erreur={action.erreursChamps.find((x) => x.champ === 'montant')?.message} />
              <Selection id="guichet-prestataire" libelle="Prestataire" optionVide="Premier prestataire configuré" facultatif options={(prestataires.data ?? []).map((p) => ({ valeur: p.prestataire, libelle: `${p.prestataire} (${libelle(LIBELLES_ETAT_PRESTATAIRE, p.etat)})` }))} value={prestataire} onChange={(e) => setPrestataire(e.target.value)} />
            </GrilleChamps>
            {prestataires.data && prestataires.data.length === 0 ? <Bandeau type="attention">Aucun prestataire de paiement en ligne n&apos;est configuré sur ce serveur.</Bandeau> : null}
            {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
          </>
        )}
      </Dialogue>
    </>
  );
}
